import type { EncryptedPayload } from "./types";
import { loadLocalRoutePayload, saveLocalRoutePayload } from "./storage";

const ROUTE_API = "/api/route";

const isEncryptedPayload = (value: unknown): value is EncryptedPayload => {
  if (!value || typeof value !== "object") return false;
  const payload = value as Record<string, unknown>;
  return (
    payload.version === 1 &&
    payload.algorithm === "AES-GCM" &&
    payload.kdf === "PBKDF2-SHA-256" &&
    typeof payload.iterations === "number" &&
    typeof payload.salt === "string" &&
    typeof payload.iv === "string" &&
    typeof payload.data === "string"
  );
};

export type SaveRouteResult =
  | { ok: true; location: "server" | "local" }
  | { ok: false; message: string; status?: number };

export const fetchRoutePayload = async (): Promise<EncryptedPayload | null> => {
  const response = await fetch(ROUTE_API, {
    cache: "no-store",
  }).catch(() => null);

  if (response?.ok) {
    const payload = await response.json().catch(() => null);
    if (isEncryptedPayload(payload)) {
      return payload;
    }
  }

  return (await loadLocalRoutePayload()) ?? null;
};

export const saveRoutePayload = async (
  payload: EncryptedPayload,
): Promise<SaveRouteResult> => {
  const response = await fetch(ROUTE_API, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  }).catch(() => null);

  if (response?.ok) {
    await saveLocalRoutePayload(payload);
    return { ok: true, location: "server" };
  }

  await saveLocalRoutePayload(payload);
  return { ok: true, location: "local" };
};
